/**
 * ABOUTME: Chat card component for displaying ROI summary data inline in chat.
 * Rendered when the analytics agent returns ROI data via message.metadata.roi_summary.
 */
import { ArrowUp, ArrowDown, DollarSign, Users, Target } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CHAT_CARD } from '@/components/chat/chatCard'
import { formatDateRange } from '@/components/chat/chatCardDate'

interface ROISummaryData {
  total_revenue: number
  total_ad_spend: number
  overall_roas: number | null
  overall_roi_percent: number | null
  avg_cost_per_lead: number | null
  avg_cost_per_conversion: number | null
  total_conversions: number
  total_leads: number
  period_start: string
  period_end: string
}

interface ROISummaryCardProps {
  data: ROISummaryData
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function formatPercent(value: number | null): string {
  if (value == null) return '--'
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value)
}

function MetricRow({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: React.ElementType
  label: string
  value: string
  sub?: string
}) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2">
        <Icon className="text-muted-foreground size-3.5" />
        <span className="text-muted-foreground text-xs">{label}</span>
      </div>
      <div className="text-right">
        <span className="text-sm font-semibold">{value}</span>
        {sub && <span className="text-muted-foreground ml-1.5 text-xs">{sub}</span>}
      </div>
    </div>
  )
}

export function ROISummaryCard({ data }: ROISummaryCardProps) {
  const roiPositive = data.overall_roi_percent != null ? data.overall_roi_percent >= 0 : null
  const TrendArrow = roiPositive ? ArrowUp : ArrowDown

  // Power-user subtitle (only shown when at least one acronym value exists)
  const subtitleParts: string[] = []
  if (data.overall_roas != null) {
    subtitleParts.push(`ROAS ${data.overall_roas.toFixed(2)}x`)
  }
  if (data.overall_roi_percent != null) {
    subtitleParts.push(`ROI ${formatPercent(data.overall_roi_percent)}`)
  }
  const subtitle = subtitleParts.join(' · ')

  return (
    <div className={cn(CHAT_CARD, 'my-2 space-y-2')} data-testid="roi-summary-card">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Performance summary</h3>
        <span className="text-muted-foreground text-xs">
          {formatDateRange(data.period_start, data.period_end)}
        </span>
      </div>
      <div>
        {/* Headline: plain-English revenue vs spend */}
        <div className="mb-2">
          <p className="text-sm leading-snug font-medium">
            You spent <span className="font-semibold">{formatCurrency(data.total_ad_spend)}</span>,
            earned <span className="font-semibold">{formatCurrency(data.total_revenue)}</span> back.
          </p>
          {data.overall_roi_percent != null && (
            <div
              className={cn(
                'mt-1 flex items-center gap-1 text-xs font-medium',
                roiPositive ? 'text-success' : 'text-destructive'
              )}
            >
              <TrendArrow className="size-3" />
              <span>{formatPercent(data.overall_roi_percent)}</span>
              {subtitle && (
                <span className="text-muted-foreground ml-1.5 text-xs font-normal">{subtitle}</span>
              )}
            </div>
          )}
          {data.overall_roi_percent == null && subtitle && (
            <div className="text-muted-foreground mt-1 text-xs">{subtitle}</div>
          )}
        </div>

        {/* Detail rows */}
        <div className="divide-border/50 divide-y">
          <MetricRow
            icon={DollarSign}
            label="Ad spend"
            value={formatCurrency(data.total_ad_spend)}
          />
          <MetricRow
            icon={Users}
            label="Leads"
            value={formatNumber(data.total_leads)}
            sub={
              data.avg_cost_per_lead != null
                ? `${formatCurrency(data.avg_cost_per_lead)}/lead`
                : undefined
            }
          />
          <MetricRow
            icon={Target}
            label="Conversions"
            value={formatNumber(data.total_conversions)}
            sub={
              data.avg_cost_per_conversion != null
                ? `${formatCurrency(data.avg_cost_per_conversion)}/conv`
                : undefined
            }
          />
        </div>
      </div>
    </div>
  )
}
